import React, { useState, useEffect, useRef } from 'react';
import { ProfileData } from '../types/profile.types';
import ImageModal from './ImageModal';
import './Sidebar.css';
interface SidebarProps {
  profileData: ProfileData;
  onLogoChange?: (file: File) => void;
  onLogoDelete?: () => void;
  isOpen?: boolean;
  showLogo?: boolean;
  wantsToManageProfile?: boolean;
  readOnly?: boolean;
}
const Sidebar: React.FC<SidebarProps> = ({ profileData, onLogoChange, onLogoDelete, isOpen = true, showLogo = true, wantsToManageProfile = false, readOnly = false }) => {
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  // Función para validar URL
  const validateUrl = (url: string): boolean => {
    if (url.trim() === '') return false; // Campo obligatorio, no puede estar vacío
    // Si ya tiene protocolo, validar URL completa
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const urlRegex = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
      return urlRegex.test(url);
    }
    // Si no tiene protocolo, validar solo dominio
    const domainRegex = /^(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
    return domainRegex.test(url);
  };
  // Función para validar teléfono según el país
  const validatePhoneNumber = (countryCode: string, phoneNumber: string): boolean => {
    if (!phoneNumber || !phoneNumber.trim()) return false;
    // Remover todos los caracteres no numéricos
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    // Patrones de validación por país
    const patterns: { [key: string]: RegExp } = {
      'AR': /^\d{10,11}$/, // Argentina: 10-11 dígitos
      'BR': /^\d{10,11}$/, 'CL': /^\d{8,9}$/, 'CO': /^\d{10}$/, 'MX': /^\d{10}$/,
      'PE': /^\d{9}$/, 'UY': /^\d{8,9}$/, 'VE': /^\d{10,11}$/, 'BO': /^\d{8}$/,
      'EC': /^\d{9}$/, 'PY': /^\d{9}$/, 'CR': /^\d{8}$/, 'PA': /^\d{8}$/,
      'GT': /^\d{8}$/, 'HN': /^\d{8}$/, 'SV': /^\d{8}$/, 'NI': /^\d{8}$/,
      'CU': /^\d{8}$/, 'DO': /^\d{10}$/, 'HT': /^\d{8}$/, 'JM': /^\d{10}$/,
      'TT': /^\d{10}$/, 'US': /^\d{10}$/, 'CA': /^\d{10}$/, 'ES': /^\d{9}$/,
      'IT': /^\d{10,11}$/, 'FR': /^\d{10}$/, 'DE': /^\d{10,11}$/, 'GB': /^\d{10,11}$/,
      'PT': /^\d{9}$/, 'NL': /^\d{9}$/, 'BE': /^\d{9}$/, 'CH': /^\d{9}$/,
      'AT': /^\d{10,11}$/, 'SE': /^\d{9}$/, 'NO': /^\d{8}$/, 'DK': /^\d{8}$/,
      'FI': /^\d{9,10}$/, 'PL': /^\d{9}$/, 'CZ': /^\d{9}$/, 'HU': /^\d{9}$/,
      'RO': /^\d{10}$/, 'BG': /^\d{9}$/, 'GR': /^\d{10}$/, 'RU': /^\d{10,11}$/,
      'UA': /^\d{9}$/, 'IE': /^\d{9}$/, 'IS': /^\d{7}$/, 'CN': /^\d{11}$/,
      'JP': /^\d{10,11}$/, 'KR': /^\d{10,11}$/, 'IN': /^\d{10}$/, 'TH': /^\d{9}$/,
      'SG': /^\d{8}$/, 'MY': /^\d{9,10}$/, 'ID': /^\d{10,12}$/, 'PH': /^\d{10}$/,
      'VN': /^\d{9,10}$/, 'TW': /^\d{9}$/, 'HK': /^\d{8}$/, 'BD': /^\d{10}$/,
      'PK': /^\d{10}$/, 'LK': /^\d{9}$/, 'NP': /^\d{10}$/, 'MM': /^\d{9,10}$/,
      'KH': /^\d{8,9}$/, 'LA': /^\d{8,9}$/, 'MN': /^\d{8}$/, 'AU': /^\d{9,10}$/,
      'NZ': /^\d{8,9}$/, 'FJ': /^\d{7,8}$/, 'PG': /^\d{8}$/, 'NC': /^\d{6}$/,
      'ZA': /^\d{9}$/, 'EG': /^\d{10}$/, 'NG': /^\d{10,11}$/, 'KE': /^\d{9,10}$/,
      'MA': /^\d{9}$/, 'TN': /^\d{8}$/, 'DZ': /^\d{9}$/, 'GH': /^\d{9}$/,
      'ET': /^\d{9}$/, 'UG': /^\d{9}$/, 'TZ': /^\d{9}$/, 'ZW': /^\d{9}$/,
      'BW': /^\d{7,8}$/, 'NA': /^\d{9}$/, 'ZM': /^\d{9}$/, 'MW': /^\d{9}$/,
      'MZ': /^\d{9}$/, 'MG': /^\d{9}$/, 'MU': /^\d{7,8}$/, 'SC': /^\d{7}$/,
      'SA': /^\d{9}$/, 'AE': /^\d{9}$/, 'QA': /^\d{8}$/, 'KW': /^\d{8}$/,
      'BH': /^\d{8}$/, 'OM': /^\d{8}$/, 'JO': /^\d{9}$/, 'LB': /^\d{8}$/,
      'SY': /^\d{9}$/, 'IQ': /^\d{10}$/, 'IR': /^\d{10}$/, 'IL': /^\d{9}$/,
      'TR': /^\d{10}$/
    };
    const pattern = patterns[countryCode];
    return pattern ? pattern.test(cleanNumber) : cleanNumber.length >= 7 && cleanNumber.length <= 15;
  };
  // Función para calcular el progreso del perfil
  const calculateProgress = (): number => {
    let completedFields = 0;
    let totalFields = 0;
    // Normalizar arrays de temáticas para asegurar que siempre sean arrays válidos
    const personalThematicAxesRaw = profileData.contactInfo?.thematicAxes;
    const orgThematicAxesRaw = profileData.basicInfo?.thematicAxes;
    // Normalizar y limpiar arrays de temáticas
    const personalThematicAxesNormalized = Array.isArray(personalThematicAxesRaw) 
      ? personalThematicAxesRaw.filter((axis: any) => {
          const axisStr = String(axis);
          return axisStr && 
            axisStr.trim() !== '' && 
            axisStr !== '[' && 
            axisStr !== ']' &&
            !axisStr.startsWith('{') &&
            !axisStr.startsWith('"[');
        })
      : [];
    const orgThematicAxesNormalized = Array.isArray(orgThematicAxesRaw) 
      ? orgThematicAxesRaw.filter((axis: any) => {
          const axisStr = String(axis);
          return axisStr && 
            axisStr.trim() !== '' && 
            axisStr !== '[' && 
            axisStr !== ']' &&
            !axisStr.startsWith('{') &&
            !axisStr.startsWith('"[');
        })
      : [];
    // PERFIL PERSONAL: solo contar campos de Info Personal (11 campos)
    if (!wantsToManageProfile) {
      // Campos de Info Personal (todos obligatorios):
      // 1. Nombre
      totalFields++;
      if (profileData.contactInfo.firstName && profileData.contactInfo.firstName.toString().trim() !== '') {
        completedFields++;
      }
      // 2. Apellido
      totalFields++;
      if (profileData.contactInfo.lastName && profileData.contactInfo.lastName.toString().trim() !== '') {
        completedFields++;
      }
      // 3. Correo electrónico (siempre debe estar presente, viene del usuario autenticado)
      totalFields++;
      const email = profileData.contactInfo.email || '';
      if (email && email.toString().trim() !== '' && email.includes('@')) {
        completedFields++;
      }
      // 4. Teléfono (debe pasar la validación según el país)
      totalFields++;
      const codigoPais = profileData.contactInfo.codigoPais || 'AR';
      const numeroTelefono = profileData.contactInfo.numeroTelefono || '';
      if (numeroTelefono && numeroTelefono.toString().trim() !== '' && validatePhoneNumber(codigoPais, numeroTelefono.toString().trim())) {
        completedFields++;
      }
      // 5. Organización
      totalFields++;
      if (profileData.basicInfo.organizationName && profileData.basicInfo.organizationName.toString().trim() !== '') {
        completedFields++;
      }
      // 6. Tipo de organización
      totalFields++;
      if (profileData.basicInfo.organizationType && profileData.basicInfo.organizationType.toString().trim() !== '') {
        completedFields++;
      }
      // 7. Cargo
      totalFields++;
      if (profileData.contactInfo.position && profileData.contactInfo.position.toString().trim() !== '') {
        completedFields++;
      }
      // 8. Área
      totalFields++;
      if (profileData.contactInfo.area && profileData.contactInfo.area.toString().trim() !== '') {
        completedFields++;
      }
      // 9. Subárea
      totalFields++;
      if (profileData.contactInfo.subarea && profileData.contactInfo.subarea.toString().trim() !== '') {
        completedFields++;
      }
      // 10. Sitio web (debe ser una URL válida sin errores)
      totalFields++;
      const website = profileData.basicInfo.website || '';
      if (website && website.toString().trim() !== '' && validateUrl(website.toString().trim())) {
        completedFields++;
      }
      // 11. Ejes de interés
      totalFields++;
      if (personalThematicAxesNormalized.length > 0) {
        completedFields++;
      }
      // Calcular porcentaje
      const percentage = totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;
      return Math.min(percentage, 100);
    }
    // PERFIL INSTITUCIONAL: solo contar campos de Info de Organización y Modalidad de Participación
    // NO incluir campos de Info Personal
    // Campos de Información de la organización:
    // 1. Participación en el programa del evento
    totalFields++;
    const field1 = profileData.basicInfo.eventProgramParticipation && profileData.basicInfo.eventProgramParticipation.length > 0;
    if (field1) completedFields++;
    // 2. Logo
    totalFields++;
    const hasLogo = profileData.basicInfo.logo && 
      ((typeof profileData.basicInfo.logo === 'string' && profileData.basicInfo.logo.trim() !== '') ||
       (profileData.basicInfo.logo instanceof File));
    const field2 = hasLogo;
    if (field2) completedFields++;
    // 3. País
    totalFields++;
    const field3 = profileData.basicInfo.country && profileData.basicInfo.country.toString().trim() !== '';
    if (field3) completedFields++;
    // 4. Ejes Temáticos de la organización (debe tener al menos un eje seleccionado)
    totalFields++;
    if (orgThematicAxesNormalized.length > 0) {
      completedFields++;
    } else {
    }
    // 5. Descripción
    totalFields++;
    const field5 = profileData.institutionalDescription.description && 
        profileData.institutionalDescription.description.toString().trim() !== '';
    if (field5) completedFields++;
    // Campos de Modalidad de Participación:
    // 6. Modalidades de participación seleccionadas
    totalFields++;
    const field6 = profileData.participationModalities && profileData.participationModalities.length > 0;
    if (field6) completedFields++;
    // Calcular porcentaje
    const percentage = totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;
    return Math.min(percentage, 100); // Asegurar que no exceda 100%
  };
  const progressPercentage = calculateProgress();
  
  // Mantener referencia al logo anterior para comparar
  const previousLogoRef = React.useRef<string | File | undefined>(profileData.basicInfo.logo);
  
  useEffect(() => {
    const currentLogo = profileData.basicInfo.logo;
    const previousLogo = previousLogoRef.current;
    
    // Log para debugging
    
    // Solo actualizar si el logo realmente cambió (no solo si profileData cambió)
    // Comparar valores: si son iguales, no actualizar el preview
    const logosAreEqual = 
      currentLogo === previousLogo || 
      (typeof currentLogo === 'string' && typeof previousLogo === 'string' && currentLogo.trim() === previousLogo.trim()) ||
      (currentLogo === undefined && previousLogo === undefined) ||
      (currentLogo === null && previousLogo === null);
    
    if (logosAreEqual) {
      // El logo no cambió, mantener el preview actual
      return;
    }
    
    // El logo cambió, actualizar el preview
    previousLogoRef.current = currentLogo;
    
    // Si el logo es un File, crear una URL de preview
    if (currentLogo && typeof currentLogo !== 'string') {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(currentLogo);
    } else if (typeof currentLogo === 'string' && currentLogo.trim() !== '') {
      // Convertir URL de Supabase a URL pública si es necesario
      let logoUrl = currentLogo.trim();
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
      
      
      // Si es una data URL (base64), usarla directamente
      if (logoUrl.startsWith('data:image/')) {
        setLogoPreview(logoUrl);
        return;
      }
      
      // Si ya es una URL completa válida (http/https), procesarla
      if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
        // Si es una URL de Supabase signed, convertirla a pública
        if (logoUrl.includes('supabase.co') && logoUrl.includes('/storage/v1/object/sign/')) {
          const match = logoUrl.match(/\/storage\/v1\/object\/sign\/([^?]+)/);
          if (match && supabaseUrl) {
            const filePath = match[1];
            // Extraer el bucket y el path del archivo
            const pathParts = filePath.split('/');
            if (pathParts.length >= 2) {
              const bucket = pathParts[0];
              const fileName = pathParts.slice(1).join('/');
              logoUrl = `${supabaseUrl}/storage/v1/object/public/${bucket}/${fileName}`;
            } else {
              // Si no tiene bucket explícito, asumir que es logos
              logoUrl = `${supabaseUrl}/storage/v1/object/public/perfil-institucional/logos/${filePath}`;
            }
          }
        }
        // Si ya es una URL pública de Supabase, verificar que tenga el dominio completo
        else if (logoUrl.includes('/storage/v1/object/public/')) {
          // Ya está en formato público con dominio, mantenerla
        }
        // Si es una URL de Supabase pero no tiene /storage/v1/object/public/, puede ser una URL incompleta
        else if (logoUrl.includes('supabase.co') && !logoUrl.includes('/storage/')) {
          // Intentar construir la URL pública si tenemos el path
          // Esto puede pasar si la URL viene incompleta del backend
        }
      }
      // Si es una ruta relativa que empieza con /storage/files/, convertirla a URL completa
      else if (logoUrl.startsWith('/storage/files/')) {
        // Preservar la capitalización original del path
        const filePath = logoUrl.replace('/storage/files/', '');
        if (supabaseUrl) {
          // Construir la URL manteniendo la capitalización exacta del path
          logoUrl = `${supabaseUrl}/storage/v1/object/public/${filePath}`;
        }
      }
      // Si es una ruta relativa que empieza con /storage/v1/object/public/, agregar dominio
      else if (logoUrl.startsWith('/storage/v1/object/public/')) {
        if (supabaseUrl) {
          logoUrl = `${supabaseUrl}${logoUrl}`;
        }
      }
      // Si es solo un nombre de archivo o ruta relativa sin /storage/files/, intentar construir la URL
      else if (!logoUrl.includes('/') || logoUrl.split('/').length <= 2) {
        // Asumir que es un archivo en el bucket de logos
        if (supabaseUrl) {
          logoUrl = `${supabaseUrl}/storage/v1/object/public/perfil-institucional/logos/${logoUrl}`;
        }
      }
      
      
      // Solo establecer el preview si la URL es válida
      if (logoUrl && (logoUrl.startsWith('http://') || logoUrl.startsWith('https://') || logoUrl.startsWith('data:image/'))) {
        setLogoPreview(logoUrl);
      } else {
        // Si no es válida, intentar mantener el preview anterior si existe y es una data URL
        // Esto evita que el logo desaparezca cuando se actualiza con una URL inválida
        if (logoPreview && (logoPreview.startsWith('data:image/') || logoPreview.startsWith('http://') || logoPreview.startsWith('https://'))) {
          // Mantener el preview anterior - no actualizar
          return;
    } else {
          // Solo limpiar si realmente no hay preview anterior válido
        setLogoPreview(null);
        }
      }
    } else {
      // Logo es undefined, null, o string vacío - limpiar el preview solo si realmente cambió
      // Pero solo si el logo anterior también era null/undefined
      if (previousLogo !== undefined && previousLogo !== null && previousLogo !== '') {
        setLogoPreview(null);
      } else {
      }
    }
  }, [profileData.basicInfo.logo]);
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onLogoChange) {
      onLogoChange(file);
    } else {
    }
    // Resetear el input para permitir seleccionar el mismo archivo nuevamente
    if (e.target) {
      e.target.value = '';
    }
  };
  const handleLogoDelete = async () => {
    // Limpiar el preview local
    setLogoPreview(null);
    // Llamar al callback para actualizar formData en ProfileForm
    if (onLogoDelete) {
      onLogoDelete();
    }
  };
  // Los tipos de organización ahora son strings directos del form participar
  // Si el valor ya es un string completo, usarlo directamente; si no, intentar mapear
  const getOrganizationTypeLabel = (type: string): string => {
    if (!type) return 'No especificado';
    // Si ya es un string completo (como "Empresa / PyME"), devolverlo tal cual
    if (type.includes('/') || type.length > 15) {
      return type;
    }
    // Mapeo para compatibilidad con valores antiguos
    const oldTypeMap: Record<string, string> = {
      'empresa': 'Empresa',
      'ong': 'ONG',
      'gobierno': 'Gobierno',
      'startup': 'Startup',
      'universidad': 'Universidad',
      'organismo_internacional': 'Organismo Internacional',
      'camara': 'Cámara',
      'asociacion': 'Asociación',
      'otro': 'Otro'
    };
    return oldTypeMap[type] || type;
  };
  return (
    <div className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
      <div className="sidebar-content">
        <div className="sidebar-header">
          <h2>{wantsToManageProfile ? 'Perfil Institucional' : 'Perfil Personal'}</h2>
          <p className="sidebar-subtitle">Podrás guardar el perfil en formato borrador y continuar realizando modificaciones</p>
        </div>
        {showLogo && (
          <div className="logo-section">
            <div className="logo-container">
              {logoPreview ? (
                <img 
                  src={logoPreview} 
                  alt="Logo de la organización" 
                  className="logo-preview"
                  onClick={() => setShowImageModal(true)}
                  style={{ cursor: 'pointer' }}
                  onLoad={() => {
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    // Intentar usar la URL original si la procesada falla
                    if (logoPreview && logoPreview.startsWith('https://') && logoPreview.includes('supabase.co')) {
                      // La URL puede estar correcta pero la imagen no existe o hay un problema de CORS
                      // No limpiar el preview - mantenerlo para que el usuario vea que hay un logo
                    }
                  }}
                />
              ) : (
                <div className="logo-placeholder">
                  <p>Logo</p>
                </div>
              )}
            </div>
            {!readOnly && (
              <>
                <button 
                  className="logo-upload-btn"
                  onClick={() => {
                    if (logoPreview) {
                      setShowImageModal(true);
                    } else {
                      fileInputRef.current?.click();
                    }
                  }}
                >
                  {logoPreview ? 'Editar Logo' : 'Subir Logo'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/svg+xml,image/svg,.svg,.ai,.eps,application/postscript,application/illustrator"
                  onChange={handleLogoChange}
                  style={{ display: 'none' }}
                />
                <p className="logo-instruction-text">
                  Adjuntar logo de la organización en formato PNG o curvas editable
                </p>
              </>
            )}
          </div>
        )}
        {logoPreview && (
          <ImageModal
            isOpen={showImageModal}
            imageUrl={logoPreview}
            alt="Logo de la organización"
            onClose={() => setShowImageModal(false)}
            onSave={async (editedImageUrl) => {
              // Convertir la URL del blob a File
              try {
                // La URL puede ser blob, data, o una URL de WordPress
                // Si es una URL de WordPress, necesitamos convertirla a blob primero
                if (!editedImageUrl) {
                  // Error silencioso - no mostrar diálogo al usuario
                  return;
                }
                let blob: Blob;
                if (editedImageUrl.startsWith('blob:') || editedImageUrl.startsWith('data:')) {
                  // Es un blob o data URL, hacer fetch directamente
                  const response = await fetch(editedImageUrl);
                  if (!response.ok) {
                    throw new Error(`Error al cargar la imagen: ${response.status} ${response.statusText}`);
                  }
                  blob = await response.blob();
                } else {
                  // Es una URL de WordPress, usar el proxy para evitar CORS
                  let proxyBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;
                  proxyBaseUrl = proxyBaseUrl.replace(/\/api\/?$/, '');
                  const proxyUrl = `${proxyBaseUrl}/api/proxy-image?url=${encodeURIComponent(editedImageUrl)}`;
                  const response = await fetch(proxyUrl);
                  if (!response.ok) {
                    throw new Error(`Error al cargar la imagen: ${response.status} ${response.statusText}`);
                  }
                  blob = await response.blob();
                }
                // Extraer el nombre original del archivo de la URL original
                const getOriginalFileName = (url: string): string => {
                  try {
                    // Si es una URL de WordPress, extraer el nombre del archivo
                    if (url && url.includes('/')) {
                      const urlParts = url.split('/');
                      const fileName = urlParts[urlParts.length - 1];
                      // Remover parámetros de query si existen
                      const fileNameWithoutParams = fileName.split('?')[0];
                      // Si tiene nombre válido, usarlo
                      if (fileNameWithoutParams && fileNameWithoutParams.includes('.')) {
                        return fileNameWithoutParams;
                      }
                    }
                    // Si es un blob o data URL, usar nombre por defecto
                    return 'logo.png';
                  } catch {
                    return 'logo.png';
                  }
                };
                const originalFileName = getOriginalFileName(logoPreview || '');
                // Extraer nombre y extensión
                const lastDotIndex = originalFileName.lastIndexOf('.');
                let fileNameWithoutExt = originalFileName;
                let fileExtension = '.png';
                if (lastDotIndex > 0) {
                  fileNameWithoutExt = originalFileName.substring(0, lastDotIndex);
                  fileExtension = originalFileName.substring(lastDotIndex);
                }
                // Agregar "-edited" antes de la extensión
                const editedFileName = `${fileNameWithoutExt}-edited${fileExtension}`;
                // Crear el archivo con el nombre original + "edited" y extensión .png para que pase la validación de formato
                // El tipo puede ser image/jpeg o image/png, pero la extensión debe ser .png
                const file = new File([blob], editedFileName, { type: 'image/png' });
                // Actualizar el preview
                setLogoPreview(editedImageUrl);
                // Actualizar la referencia para evitar que el useEffect lo sobrescriba
                previousLogoRef.current = editedImageUrl;
                // Subir la imagen editada
                if (onLogoChange) {
                  await onLogoChange(file);
                } else {
                }
                // Cerrar el modal después de guardar
                setShowImageModal(false);
              } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
                // Error silencioso - no mostrar diálogo al usuario
              }
            }}
            allowEdit={!readOnly}
            isLogo={true}
            onLogoChange={async (file: File) => {
              // Actualizar el preview inmediatamente
              const reader = new FileReader();
              reader.onloadend = () => {
                const previewUrl = reader.result as string;
                setLogoPreview(previewUrl);
                // Actualizar la referencia para evitar que el useEffect lo sobrescriba
                previousLogoRef.current = previewUrl;
              };
              reader.readAsDataURL(file);
              // Subir el archivo usando el callback
              if (onLogoChange) {
                await onLogoChange(file);
              }
            }}
            onLogoDelete={handleLogoDelete}
          />
        )}
        <div className="basic-info-summary">
          <div className="info-item">
            <span className="info-label">Nombre de la organización:</span>
            <span className="info-value">
              {profileData.basicInfo.organizationName || 'Sin nombre'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Tipo de organización:</span>
            <span className="info-value">
              {getOrganizationTypeLabel(profileData.basicInfo.organizationType) || 'No especificado'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Correo electrónico:</span>
            <span className="info-value">
              {profileData.contactInfo.email || 'No especificado'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Cargo:</span>
            <span className="info-value">
              {profileData.contactInfo.position || 'No especificado'}
            </span>
          </div>
        </div>
        <div className="progress-section">
          <h3>{wantsToManageProfile ? 'Progreso de Perfil Institucional' : 'Progreso del Perfil Personal'}</h3>
          <p className="progress-legend">
            {wantsToManageProfile ? (
              <>Para validar tu perfil, completa el <strong className="progress-strong">logo de la organización</strong> y todos los campos obligatorios (<span className="progress-asterisk">*</span>) de las pestañas <strong className="progress-strong">Información de la organización</strong> y <strong className="progress-strong">Modalidad de participación</strong>.</>
            ) : (
              <>Una vez completados todos los campos obligatorios (<span className="progress-asterisk">*</span>) de la pestaña <strong className="progress-strong">Perfil Personal</strong> se dará como válida su acreditación al evento.</>
            )}
          </p>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
          <p className="progress-text">
            {progressPercentage}% completado
          </p>
        </div>
      </div>
    </div>
  );
};
export default Sidebar;
